// react
import React, { useEffect, useState } from "react";

// prop-types
import PropTypes from "prop-types";

// context
import { UserAuth } from "context/AuthContext";

// qrcode.react
import { QRCodeSVG } from "qrcode.react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  IconButton,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// components
import { Puller } from "components/common/Puller";
import { LoadingDialog } from "components/loading/LoadingComponent";

// config
import documentTypeLabels from "../../../config/documentTypeLabels.json";

// lib/safetwin
import { genRndStringPrintable } from "lib/safetwin/crypto/cryptolibsodium";

// features/databox
import createDataBox from "features/databox/createDataBox";

// features/user
import addSeenTag from "features/user/addSeenTag";

// A ---------------------------------------------------------------------- M

const CreateDataboxWizardDialog = ({ open, setOpen, handleSuccessful }) => {
  const { user, conservSostL1 } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [tipologiaDocumentale, setTipologiaDocumentale] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [tagID, setTagID] = useState("");
  const [isCreatingDatabox, setIsCreatingDatabox] = useState(false);

  const steps = [
    { label: t("name"), description: t("new_databox_1") },
    { label: t("description"), description: t("new_databox_2") },
    conservSostL1 && { label: t("document_type"), description: t("new_databox_3") },
    { label: t("qr_code"), description: conservSostL1 ? t("new_databox_5") : t("new_databox_4") },
  ].filter(Boolean);

  useEffect(() => {
    const handlePopstate = () => open && handleReset();
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  const handleReset = () => {
    setName("");
    setNotes("");
    setTipologiaDocumentale("");
    setActiveStep(0);
    setTagID("");
    setIsCreatingDatabox(false);
    setOpen(false);
  };

  const handleSave = async () => {
    setIsCreatingDatabox(true);

    try {
      await createDataBox(tagID, user.uid, name, notes, conservSostL1 ? tipologiaDocumentale : undefined);
      await addSeenTag(tagID, user.uid, name, notes);
      handleSuccessful();
    } catch (error) {
      console.log("Error in handleSave:", error);
    } finally {
      setIsCreatingDatabox(false);
      handleReset();
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const generateNewString = () => setTagID(genRndStringPrintable(10));

  const stepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              required
              margin="dense"
              label={t("name")}
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              sx={{
                mt: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                },
              }}
            />
            <DialogActions>
              <Button
                fullWidth={isMobile}
                variant="contained"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={handleNext}
                disabled={!name}
              >
                {t("continue")}
              </Button>
            </DialogActions>
          </>
        );
      case 1:
        return (
          <>
            <TextField
              fullWidth
              margin="dense"
              label={t("description")}
              variant="outlined"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              sx={{
                mt: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                },
              }}
            />
            <DialogActions>
              <Button
                fullWidth={isMobile}
                variant="text"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={handleBack}
              >
                {t("back")}
              </Button>
              <Button
                fullWidth={isMobile}
                variant="contained"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={() => {
                  generateNewString();
                  handleNext();
                }}
              >
                {t("continue")}
              </Button>
            </DialogActions>
          </>
        );
      case 2:
        if (conservSostL1) {
          return (
            <>
              <FormControl
                fullWidth
                required
                margin="dense"
                sx={{
                  mt: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                  },
                }}
              >
                <Select value={tipologiaDocumentale} onChange={(e) => setTipologiaDocumentale(e.target.value)}>
                  {Object.entries(documentTypeLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DialogActions>
                <Button
                  fullWidth={isMobile}
                  variant="text"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                  onClick={handleBack}
                >
                  {t("back")}
                </Button>
                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                  onClick={() => {
                    generateNewString();
                    handleNext();
                  }}
                  disabled={!tipologiaDocumentale}
                >
                  {t("continue")}
                </Button>
              </DialogActions>
            </>
          );
        } else {
          return (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  my: 3,
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                }}
              >
                <QRCodeSVG value={`${process.env.REACT_APP_ENVIRONMENT_URL}/${tagID}`} style={{ height: 160, width: 160 }} />
              </Box>
              <DialogActions>
                <Button
                  fullWidth={isMobile}
                  variant="text"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                  onClick={handleBack}
                >
                  {t("back")}
                </Button>
                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                  onClick={handleSave}
                >
                  {t("confirm")}
                </Button>
              </DialogActions>
            </>
          );
        }
      case 3:
        return (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                my: 3,
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
              }}
            >
              <QRCodeSVG value={`${process.env.REACT_APP_ENVIRONMENT_URL}/${tagID}`} style={{ height: 160, width: 160 }} />
            </Box>
            <DialogActions>
              <Button
                fullWidth={isMobile}
                variant="text"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={handleBack}
              >
                {t("back")}
              </Button>
              <Button
                fullWidth={isMobile}
                variant="contained"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={handleSave}
              >
                {t("confirm")}
              </Button>
            </DialogActions>
          </>
        );
      default:
        return null;
    }
  };

  const renderStepContent = steps.map((step, index) => (
    <Step key={step.label}>
      <StepLabel
        sx={{
          "& .MuiStepLabel-label": {
            color: theme.palette.text.secondary,
            fontWeight: 600,
            "&.Mui-active": {
              color: theme.palette.primary.main,
            },
            "&.Mui-completed": {
              color: theme.palette.primary.dark,
            },
          },
        }}
      >
        {t(step.label)}
      </StepLabel>
      <StepContent>
        <Typography
          sx={{
            color: (theme) => theme.palette.text.secondary,
            mb: 1.5,
          }}
        >
          {t(step.description)}
        </Typography>
        {stepContent(index)}
      </StepContent>
    </Step>
  ));

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight={700}>
          {conservSostL1 ? t("new_archive") : t("new_databox")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("close")} placement="top">
          <IconButton onClick={handleReset} color="error" edge="end">
            <CloseOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const innerContent = (
    <Grid container>
      <Grid item xs={12}>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={{
            "& .MuiStepConnector-line": { borderLeftWidth: 1.5, borderColor: theme.palette.divider },
            "& .MuiStepLabel-label": { fontWeight: 600, color: theme.palette.text.primary },
          }}
        >
          {renderStepContent}
        </Stepper>
      </Grid>
    </Grid>
  );

  if (isCreatingDatabox) {
    return <LoadingDialog open={isCreatingDatabox} />;
  }

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleReset}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "6%",
          borderTopRightRadius: "6%",
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(12px)",
          boxShadow: "0 -6px 24px rgba(0,0,0,0.3)",
        },
      }}
      data-testid="databox-create-modal"
    >
      <Puller />

      <Box sx={{ p: 3 }}>
        <Grid container alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {conservSostL1 ? t("new_archive") : t("new_databox")}
          </Typography>
        </Grid>
        {innerContent}
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
      data-testid="databox-create-modal"
    >
      <DialogTitle>
        <DialogTitleContent />
      </DialogTitle>

      <DialogContent>{innerContent}</DialogContent>
    </Dialog>
  );
};

CreateDataboxWizardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  handleSuccessful: PropTypes.func.isRequired,
};

export default CreateDataboxWizardDialog;
